/** Insights → Reports (item 63): report templates that run on a schedule (or
 *  by hand) and the library of every report they produced - on ONE page
 *  (item 78, Segev 2026-11-01; there is no sub-tab, and an old `?show=templates`
 *  link lands here): the report templates first, one compact row each (what
 *  it covers, when it repeats, the next and the last report, Run now,
 *  Pause / Resume, Edit, Delete), then the Library - a reading room, the list
 *  beside the open report; `?report=<id>` opens one, which is where the bell
 *  lands. The actions (Run a report now, New report template) and the
 *  library's filters stay at the top while the page scrolls (InsightsStickyBar,
 *  item 71). The editor is a centred window; Run now asks which days to cover
 *  and shows them. Reports are the person's own: nobody else's are ever listed. */
import { useCallback, useEffect, useState } from 'react';
import { DatePicker, Dropdown, Spin, Tooltip, message as antdMessage } from 'antd';
import {
  CaretRightOutlined, DeleteOutlined, EditOutlined, MoreOutlined, PauseOutlined, PlusOutlined, ScheduleOutlined,
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { ConnectorDialog } from '@modules/chatbots/components/ConnectorDialog';
import insightsService from '../../services/insightsService';
import type { LibraryReport, ReportTemplate, ReportWindow, RunNowPeriod, TenantNode } from '../../types/insights';
import { ReportLibrary } from './ReportLibrary';
import { ReportTemplateEditor } from './ReportTemplateEditor';
import { instantLabel, reportError, scheduleLabel, statusReason, windowLabel } from './reportParts';
import { scopeSummary } from '../templates/templateParts';
import './Reports.css';

const PERIODS: RunNowPeriod[] = ['scheduled', 'previous_week', 'last_7_days', 'last_30_days', 'previous_month', 'custom'];

interface Props {
  tenantTree: TenantNode[];
  agentOptions: { value: string; label: string }[];
}

export function ReportsTab({ tenantTree, agentOptions }: Props) {
  const { t, language } = useLanguage();
  // A report covers what its owner can see: their organization and the ones under it.
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('report');
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editor, setEditor] = useState<{ template: ReportTemplate | null } | null>(null);
  const [runFor, setRunFor] = useState<ReportTemplate | null | 'pick'>(null);
  const [deleting, setDeleting] = useState<ReportTemplate | null>(null);

  /** Change several URL params in ONE update: two functional updates in one
   *  event both start from the render's params, so the second undoes the first. */
  const setParams = useCallback((changes: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([key, value]) => {
        if (value) next.set(key, value); else next.delete(key);
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const loadTemplates = useCallback(() => {
    setLoadingTemplates(true);
    insightsService.listReportTemplates()
      .then(setTemplates)
      .catch((err) => antdMessage.error(reportError(t, err)))
      .finally(() => setLoadingTemplates(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  // The report templates were a sub-tab once: its links land on this one page.
  useEffect(() => {
    if (searchParams.has('show')) setParams({ show: null });
  }, [searchParams, setParams]);

  /** Every run starts here - Run now, and Try again on a report that failed. */
  const startRun = async (templateId: string, body: { period: RunNowPeriod; start?: string; end?: string }) => {
    try {
      const report = await insightsService.runReportTemplate(templateId, body);
      antdMessage.success(t('insights.reports.run.started'));
      setRunFor(null);
      setRefreshKey((k) => k + 1);
      // The new report is the one shown: its "being written" state, then the report.
      setParams({ report: report.report_id });
      loadTemplates();
    } catch (err) {
      antdMessage.error(reportError(t, err));
    }
  };

  const runNow = (template: ReportTemplate, period: RunNowPeriod, range?: [Dayjs, Dayjs]) => startRun(
    template.template_id, { period, ...(period === 'custom' && range
      ? { start: range[0].format('YYYY-MM-DD'), end: range[1].format('YYYY-MM-DD') } : {}) });

  // The same report template, the same days.
  const retry = async (report: LibraryReport) => {
    if (report.template_id && report.period) {
      await startRun(report.template_id, { period: 'custom', start: report.period.start, end: report.period.end });
    }
  };

  const pause = async (template: ReportTemplate, paused: boolean) => {
    try {
      const updated = await insightsService.setReportTemplatePaused(template.template_id, paused);
      setTemplates((prev) => prev.map((x) => (x.template_id === updated.template_id ? updated : x)));
      antdMessage.success(t(paused ? 'insights.reports.templates.pausedDone' : 'insights.reports.templates.resumedDone',
        { name: template.name }));
    } catch (err) {
      antdMessage.error(reportError(t, err));
    }
  };

  const remove = async (template: ReportTemplate) => {
    setDeleting(null);
    try {
      await insightsService.deleteReportTemplate(template.template_id);
      setTemplates((prev) => prev.filter((x) => x.template_id !== template.template_id));
    } catch (err) {
      antdMessage.error(reportError(t, err));
    }
  };

  const actions = (
    <div className="rp-bar__group">
      {templates.length > 0 && (
        <button type="button" className="cx-btn" onClick={() => setRunFor('pick')} data-testid="reports-run-now"
                aria-label={t('insights.reports.run.button')}>
          <CaretRightOutlined aria-hidden="true" /> <span className="rp-hide-phone">{t('insights.reports.run.button')}</span>
        </button>
      )}
      <button type="button" className="cx-btn cx-btn--primary" onClick={() => setEditor({ template: null })}
              data-testid="reports-new-template">
        <PlusOutlined aria-hidden="true" /> <span className="rp-hide-phone">{t('insights.reports.templates.new')}</span>
        <span className="rp-show-phone">{t('insights.reports.templates.newShort')}</span>
      </button>
    </div>
  );

  // What runs and when: one compact row per report template, above the library.
  const templatesSection = (
    <section className="rp-area" aria-labelledby="rp-templates-title" data-testid="reports-templates-section">
      <h2 id="rp-templates-title" className="rp-area__title">
        {t('insights.reports.templates.tab')}
        {!loadingTemplates && <span className="rp-area__count"><bdi>{templates.length}</bdi></span>}
        <span className="rp-area__hint">{t('insights.reports.templates.sectionHint')}</span>
      </h2>
      {loadingTemplates ? (
        <div className="rp-tpls" aria-hidden="true">
          {[0, 1].map((i) => <div key={i} className="rp-tpl rp-tpl--skeleton"><span className="tpl-skel rp-skel--line" /></div>)}
        </div>
      ) : templates.length === 0 ? (
        <div className="rp-tpls__empty" data-testid="reports-templates-empty">
          <ScheduleOutlined aria-hidden="true" />
          {/* One action on the page: the filter row's New report template, which this line names as
              the button reads (a phone shows its short label). */}
          <span>{t('insights.reports.templates.emptyLine')} {t('insights.reports.templates.emptyBody')}{' '}
            <span className="rp-hide-phone">{t('insights.reports.templates.emptyHint', { action: t('insights.reports.templates.new') })}</span>
            <span className="rp-show-phone">{t('insights.reports.templates.emptyHint', { action: t('insights.reports.templates.newShort') })}</span>
          </span>
        </div>
      ) : (
        <div className="rp-tpls" role="table" aria-labelledby="rp-templates-title">
          <div className="rp-tpl rp-tpl--head" role="row">
            <span role="columnheader">{t('insights.reports.templates.tab')}</span>
            <span role="columnheader">{t('insights.reports.templates.colRepeats')}</span>
            <span role="columnheader">{t('insights.reports.templates.colNext')}</span>
            <span role="columnheader">{t('insights.reports.templates.colLast')}</span>
            <span role="columnheader" className="tpl-sr">{t('insights.reports.reader.more')}</span>
          </div>
          {templates.map((tpl) => {
            const paused = tpl.status === 'paused';
            const last = tpl.last_report;
            const scheduled = tpl.frequency !== 'none';
            return (
              <div key={tpl.template_id} role="row" className={`rp-tpl${paused ? ' is-paused' : ''}`}
                   data-testid="report-template-card">
                <span role="cell" className="rp-tpl__name">
                  <span className="rp-tpl__title">
                    <bdi dir="auto">{tpl.name}</bdi>
                    <span className="rp-lang" title={t(`insights.reports.templates.languageName.${tpl.language}`)}>
                      {t(`insights.reports.templates.languageBadge.${tpl.language}`)}
                    </span>
                  </span>
                  <span className="rp-tpl__covers"><bdi dir="auto">{scopeSummary(t, tpl.scope, tenantTree, user?.tenant_name)}</bdi></span>
                </span>
                <span role="cell" className="rp-tpl__pills">
                  <span className={`rp-pill ${!scheduled || paused ? 'is-muted' : ''}`}>
                    <ScheduleOutlined aria-hidden="true" /> {scheduleLabel(t, tpl, language)}
                  </span>
                  {paused && (
                    <span className={`rp-pill ${tpl.paused_reason ? 'is-problem' : 'is-paused'}`}>
                      <PauseOutlined aria-hidden="true" /> {t('insights.reports.templates.paused')}
                    </span>
                  )}
                </span>
                <span role="cell" className="rp-tpl__fact">
                  <span className="rp-tpl__label">{t('insights.reports.templates.colNext')}</span>
                  {paused && tpl.paused_reason ? (
                    <span className="is-problem">{statusReason(t, tpl.paused_reason)}</span>
                  ) : !paused && tpl.next_runs[0] ? t('insights.reports.templates.nextValue', {
                    when: instantLabel(tpl.next_runs[0].at, tpl.zone),
                    days: windowLabel(tpl.next_runs[0].covers, { year: false }),
                  }) : <span className="rp-tpl__none">—</span>}
                </span>
                <span role="cell" className="rp-tpl__fact">
                  <span className="rp-tpl__label">{t('insights.reports.templates.colLast')}</span>
                  {last ? (
                    <span className={last.status === 'failed' || last.status === 'skipped' ? 'is-problem' : undefined}>
                      {t(`insights.reports.templates.lastValue.${last.status}`,
                        { when: instantLabel(last.finished_at || last.created_at) })}
                    </span>
                  ) : <span className="rp-tpl__none">—</span>}
                </span>
                <span role="cell" className="rp-tpl__actions">
                  <button type="button" className="cx-btn cx-btn--sm" onClick={() => setRunFor(tpl)}
                          data-testid="report-template-run">
                    <CaretRightOutlined aria-hidden="true" /> {t('insights.reports.templates.run')}
                  </button>
                  {scheduled && (
                    <button type="button" className="cx-btn cx-btn--sm" onClick={() => pause(tpl, !paused)}
                            data-testid="report-template-pause">
                      {t(paused ? 'insights.reports.templates.resume' : 'insights.reports.templates.pause')}
                    </button>
                  )}
                  <button type="button" className="cx-btn cx-btn--sm" onClick={() => setEditor({ template: tpl })}
                          data-testid="report-template-edit">
                    <EditOutlined aria-hidden="true" /> {t('common.edit')}
                  </button>
                  <Dropdown trigger={['click']} placement="bottomRight" menu={{
                    items: [{ key: 'delete', label: t('common.delete'), icon: <DeleteOutlined />, danger: true }],
                    onClick: () => setDeleting(tpl),
                  }}>
                    <Tooltip title={t('insights.reports.reader.more')}>
                      <button type="button" className="cx-btn cx-btn--sm rp-icon-btn"
                              aria-label={t('insights.reports.templates.moreNamed', { name: tpl.name })}
                              data-testid="report-template-delete">
                        <MoreOutlined aria-hidden="true" />
                      </button>
                    </Tooltip>
                  </Dropdown>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );

  return (
    <div className="rp">
      <ReportLibrary
        templates={templates}
        selectedId={selectedId}
        onSelect={(id) => setParams({ report: id })}
        onOpenTemplate={(id) => {
          const template = templates.find((x) => x.template_id === id);
          if (template) setEditor({ template });
        }}
        onRetry={retry}
        refreshKey={refreshKey}
        above={templatesSection}
        actions={actions}
      />

      {editor && (
        <ReportTemplateEditor
          template={editor.template}
          tenantTree={tenantTree}
          agentOptions={agentOptions}
          onClose={() => setEditor(null)}
          onSaved={(saved, run) => {
            setEditor(null);
            loadTemplates();
            if (run) setRunFor(saved);
            else antdMessage.success(t('insights.reports.editor.saved'));
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => { if (deleting) void remove(deleting); }}
        title={deleting ? t('insights.reports.templates.deleteTitle', { name: deleting.name }) : ''}
        message={t('insights.reports.templates.deleteBody')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
      />

      {runFor && (
        <RunNowWindow templates={templates} initial={runFor === 'pick' ? null : runFor}
                      onClose={() => setRunFor(null)} onRun={runNow} />
      )}
    </div>
  );
}

/** "Run a report now": which report template (with its schedule), and which days it covers (shown). */
function RunNowWindow({ templates, initial, onClose, onRun }: {
  templates: ReportTemplate[];
  initial: ReportTemplate | null;
  onClose: () => void;
  onRun: (template: ReportTemplate, period: RunNowPeriod, range?: [Dayjs, Dayjs]) => Promise<void>;
}) {
  const { t, language } = useLanguage();
  // A paused template is still runnable by hand; the first one that runs is the natural pick.
  const first = initial || templates.find((x) => x.status === 'active') || templates[0];
  const [templateId, setTemplateId] = useState(first?.template_id);
  const template = templates.find((x) => x.template_id === templateId) || null;
  const scheduled = !!template && template.frequency !== 'none';
  const [period, setPeriod] = useState<RunNowPeriod>(scheduled ? 'scheduled' : 'previous_week');
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [busy, setBusy] = useState(false);
  // The days each choice covers, as the server will count them (the owner's zone).
  const [windows, setWindows] = useState<Partial<Record<RunNowPeriod, ReportWindow>>>({});
  useEffect(() => {
    insightsService.reportRunWindows().then(setWindows).catch(() => setWindows({}));
  }, []);

  // A by-hand template has no "as scheduled" days.
  const chosen: RunNowPeriod = !scheduled && period === 'scheduled' ? 'previous_week' : period;

  const go = async () => {
    if (!template || (chosen === 'custom' && !range)) return;
    setBusy(true);
    await onRun(template, chosen, range || undefined);
    setBusy(false);
  };

  const daysOf = (p: RunNowPeriod): string => {
    const w = windows[p];
    return w ? windowLabel(w, { year: false }) : '';
  };

  return (
    <ConnectorDialog
      title={t('insights.reports.run.title')}
      size="narrow"
      onClose={onClose}
      testId="report-run-now"
      note={t('insights.reports.run.note')}
      noteTone="muted"
      actions={(
        <>
          <button type="button" className="cx-btn" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="cx-btn cx-btn--primary" onClick={go}
                  disabled={busy || !template || (chosen === 'custom' && !range)} data-testid="report-run-go">
            {busy ? <Spin size="small" /> : <CaretRightOutlined aria-hidden="true" />} {t('insights.reports.run.go')}
          </button>
        </>
      )}
    >
      <div className="rp-form">
        <fieldset className="rp-fieldset">
          <legend className="rp-label">{t('insights.reports.run.which')}</legend>
          <div className="rp-pick" role="radiogroup" aria-label={t('insights.reports.run.which')}>
            {templates.map((x) => (
              <button key={x.template_id} type="button" role="radio" aria-checked={x.template_id === templateId}
                      className="rp-pick__opt" onClick={() => setTemplateId(x.template_id)}>
                <span className="rp-pick__title"><bdi dir="auto">{x.name}</bdi></span>
                <span className="rp-pick__hint">
                  {scheduleLabel(t, x, language)}{x.status === 'paused' ? ` · ${t('insights.reports.templates.paused')}` : ''}
                </span>
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset className="rp-fieldset">
          <legend className="rp-label">{t('insights.reports.run.period')}</legend>
          <div className="rp-pick rp-pick--compact" role="radiogroup" aria-label={t('insights.reports.run.period')}>
            {PERIODS.filter((p) => p !== 'scheduled' || scheduled).map((p) => (
              <button key={p} type="button" role="radio" aria-checked={chosen === p} className="rp-pick__opt"
                      onClick={() => setPeriod(p)} data-testid={`report-run-period-${p}`}>
                <span className="rp-pick__title">{t(`insights.reports.run.periods.${p}`)}</span>
                {windows[p] && <span className="rp-pick__hint">{daysOf(p)}</span>}
              </button>
            ))}
          </div>
          {chosen === 'custom' && (
            <DatePicker.RangePicker className="rp-range" value={range}
                                    disabledDate={(d) => d.isAfter(new Date(), 'day') || d.isSame(new Date(), 'day')}
                                    onChange={(v) => setRange(v && v[0] && v[1] ? [v[0], v[1]] : null)} />
          )}
        </fieldset>
      </div>
    </ConnectorDialog>
  );
}
